import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Flame,
  Users,
  Mail,
  LogOut,
  Trophy,
  User,
  GraduationCap,
  Bug,
  Menu,
  Map,
  Award,
  Building2,
  Shield,
  CreditCard,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { featureFlags } from "../config/featureFlags";
import Notifications from "./Notifications";
import logo from "@/assets/ithopu-logo.png";
import iTHOPULogo from "@/assets/iTHOPU.png";

export default function Navigation() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const isSchoolStudent = user?.role === "student" && !!user?.school_id;

  const NavLinks = ({ mobile = false }: { mobile?: boolean }) => (
    <>
      {/* Platform admin sees only admin dashboard */}
      {user?.role === "admin" && (
        <Link to="/admin" onClick={() => mobile && setIsOpen(false)}>
          <Button
            variant={isActive("/admin") || location.pathname.startsWith("/admin") ? "default" : "ghost"}
            className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
          >
            <Shield className="w-4 h-4" />
            Admin Dashboard
          </Button>
        </Link>
      )}

      {/* School admin sees only school dashboard */}
      {user?.role === "school_admin" && (
        <Link to="/school" onClick={() => mobile && setIsOpen(false)}>
          <Button
            variant={isActive("/school") || location.pathname.startsWith("/school") ? "default" : "ghost"}
            className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
          >
            <Building2 className="w-4 h-4" />
            School Dashboard
          </Button>
        </Link>
      )}

      {/* Hide Arena and Forge for parents, school_admin, admin, and school students (school students get a separate block below) */}
      {user?.role !== "parent" && user?.role !== "school_admin" && user?.role !== "admin" && !isSchoolStudent && (
        <>
          <Link to="/arena" onClick={() => mobile && setIsOpen(false)}>
            <Button
              variant={
                isActive("/arena") ||
                isActive("/learning-hub") ||
                isActive("/ithopu-curriculum") ||
                location.pathname.startsWith("/lesson/")
                  ? "default"
                  : "ghost"
              }
              className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
            >
              <Trophy className="w-4 h-4" />
              The Arena
            </Button>
          </Link>

          <Link to="/practice" onClick={() => mobile && setIsOpen(false)}>
            <Button
              variant={isActive("/practice") ? "default" : "ghost"}
              className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
            >
              <Flame className="w-4 h-4" />
              Training Hall
            </Button>
          </Link>

          <Link to="/competitive" onClick={() => mobile && setIsOpen(false)}>
            <Button
              variant={isActive("/competitive") ? "default" : "ghost"}
              className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
            >
              <Trophy className="w-4 h-4" />
              Tournament Hall
            </Button>
          </Link>

          <Link to="/my-roadmap" onClick={() => mobile && setIsOpen(false)}>
            <Button
              variant={isActive("/my-roadmap") ? "default" : "ghost"}
              className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
            >
              <Map className="w-4 h-4" />
              My Path
            </Button>
          </Link>

          <Link to="/certifications" onClick={() => mobile && setIsOpen(false)}>
            <Button
              variant={
                isActive("/certifications") ||
                location.pathname.startsWith("/certification-exam") ||
                location.pathname.startsWith("/certificate/")
                  ? "default"
                  : "ghost"
              }
              className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
            >
              <Award className="w-4 h-4" />
              Certifications
            </Button>
          </Link>
        </>
      )}

      {/* School students: show Arena, Training Hall, Certifications only (no Tournament Hall or My Path) */}
      {isSchoolStudent && (
        <>
          <Link to="/arena" onClick={() => mobile && setIsOpen(false)}>
            <Button
              variant={
                isActive("/arena") ||
                isActive("/learning-hub") ||
                isActive("/ithopu-curriculum") ||
                location.pathname.startsWith("/lesson/")
                  ? "default"
                  : "ghost"
              }
              className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
            >
              <Trophy className="w-4 h-4" />
              The Arena
            </Button>
          </Link>

          <Link to="/practice" onClick={() => mobile && setIsOpen(false)}>
            <Button
              variant={isActive("/practice") ? "default" : "ghost"}
              className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
            >
              <Flame className="w-4 h-4" />
              Training Hall
            </Button>
          </Link>

          <Link to="/certifications" onClick={() => mobile && setIsOpen(false)}>
            <Button
              variant={
                isActive("/certifications") ||
                location.pathname.startsWith("/certification-exam") ||
                location.pathname.startsWith("/certificate/")
                  ? "default"
                  : "ghost"
              }
              className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
            >
              <Award className="w-4 h-4" />
              Certifications
            </Button>
          </Link>
        </>
      )}

      {user?.role === "parent" && (
        <Link to="/parent-portal" onClick={() => mobile && setIsOpen(false)}>
          <Button
            variant={isActive("/parent-portal") ? "default" : "ghost"}
            className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
          >
            <Users className="w-4 h-4" />
            Parent Portal
          </Button>
        </Link>
      )}

      {user?.role === "teacher" && (
        <Link to="/teacher" onClick={() => mobile && setIsOpen(false)}>
          <Button
            variant={isActive("/teacher") ? "default" : "ghost"}
            className={`flex items-center gap-2 ${mobile ? "w-full justify-start" : ""}`}
          >
            <GraduationCap className="w-4 h-4" />
            Teacher Dashboard
          </Button>
        </Link>
      )}
    </>
  );

  return (
    <nav className="bg-white dark:bg-gray-800 shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-6">
            {/* Mobile Menu Trigger */}
            <div className="md:hidden">
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="mr-2">
                    <Menu className="h-6 w-6" />
                    <span className="sr-only">Toggle menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[80%] sm:w-[385px]">
                  <SheetHeader>
                    <SheetTitle className="text-left flex items-center gap-2">
                      <img src={logo} alt="Ithopu" className="w-8 h-8" />
                      <img src={iTHOPULogo} alt="Ithopu" className="h-6" />
                    </SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-col gap-4 mt-8">
                    <NavLinks mobile />
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            <Link
              to={user?.role === "admin" ? "/admin" : user?.role === "parent" ? "/parent-portal" : user?.role === "school_admin" ? "/school" : "/dashboard"}
              className="flex items-center gap-2"
            >
              <img
                src={logo}
                alt="Ithopu"
                className="w-10 h-10 hidden md:block"
              />
              <img src={logo} alt="Ithopu" className="w-8 h-8 md:hidden" />
              <img src={iTHOPULogo} alt="Ithopu" className="h-8" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <NavLinks />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Notifications />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span className="text-sm">{user?.username}</span>
                  {user?.is_premium && featureFlags.SHOW_PRO_BADGES && (
                    <span className="px-2 py-1 text-xs font-semibold text-white bg-gradient-to-r from-yellow-400 to-orange-500 rounded">
                      PRO
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                {featureFlags.CHARGEBEE_BILLING && (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/profile#billing"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <CreditCard className="w-4 h-4" />
                      Billing & Subscription
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link
                    to="/contact"
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Mail className="w-4 h-4" />
                    Contact Us
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/bug-report"
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Bug className="w-4 h-4" />
                    Report a Bug
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
}
